/*
 * LMMM — index FAQ pour la recherche interne : chargement + filtrage (2026-09-02).
 *
 * L'index est rendu par sections/faq-search-index.liquid (jamais inline dans une
 * page) et récupéré par la Section Rendering API `/?section_id=faq-search-index`
 * au premier focus du champ de recherche. Une seule requête par visite :
 * cache mémoire, puis sessionStorage.
 *
 * Consommateurs : assets/predictive-search.js (menu déroulant de la loupe) et
 * assets/main-search-faq.js (bloc « Dans la FAQ » de /search). Chargé par
 * layout/theme.liquid à côté de predictive-search.js et par sections/main-search.liquid ;
 * le garde-fou évite une double définition.
 *
 * Filtrage : minuscules, sans accents (NFD), mots de 3 caractères et plus, tous
 * présents dans question + réponse complète (clé `x`, plus tronquée à 30 mots
 * depuis le 2026-09-03) — un mot tapé compte s'il est le DÉBUT d'un mot du texte
 * (« livraison » trouve « livraisons », mais « port » ne trouve pas
 * « important »). Les entrées dont la question contient le plus de mots passent
 * devant, à ordre de position égal.
 *
 * Extrait justificatif (2026-09-03) : `snippet(entry, query, maxWords)` renvoie
 * une fenêtre de mots d'origine centrée sur la PREMIÈRE occurrence d'un mot de
 * la requête dans la réponse (début de la réponse si seul le titre matche), avec
 * « … » aux bords tronqués et la liste des morceaux à surligner ; `renderSnippet()`
 * la transforme en fragment DOM (nœuds texte + <mark>, jamais d'innerHTML).
 * Consommé par les deux rendus ci-dessus. Testable sous Node :
 *   node scripts/test_faq_search_index.js [chemin/faq-index.json]
 *   (index reconstruit hors boutique par scripts/export_faq_index.py, lecture seule)
 *
 * Specs : specs/2026-09-02-idee-faq-dans-recherche-interne.md
 *         specs/2026-09-03-faq-recherche-reponse-complete-extrait.md
 */
(function (root) {
  if (root.FaqSearchIndex) return;

  var SECTION_ID = 'faq-search-index';
  var STORAGE_KEY = 'lmmm-faq-search-index-v2'; // v2 (2026-09-03) : réponse complète — invalide l'index tronqué à 30 mots d'un onglet déjà ouvert
  var memory = null;
  var pending = null;

  function normalize(str) {
    return String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae');
  }

  function tokens(query) {
    return normalize(query)
      .split(/[^a-z0-9]+/)
      .filter(function (word) { return word.length >= 3; });
  }

  function hasPrefix(textTokens, word) {
    for (var i = 0; i < textTokens.length; i++) {
      if (textTokens[i].indexOf(word) === 0) return true;
    }
    return false;
  }

  function match(entries, query, max) {
    var words = tokens(query);
    if (!words.length || !Array.isArray(entries)) return [];
    var limit = max || 5;
    var hits = [];
    entries.forEach(function (entry, index) {
      var questionTokens = normalize(entry.q).split(/[^a-z0-9]+/);
      var allTokens = questionTokens.concat(normalize(entry.x).split(/[^a-z0-9]+/));
      var all = words.every(function (word) { return hasPrefix(allTokens, word); });
      if (!all) return;
      var inQuestion = words.filter(function (word) { return hasPrefix(questionTokens, word); }).length;
      hits.push({ entry: entry, score: inQuestion, index: index });
    });
    hits.sort(function (a, b) { return b.score - a.score || a.index - b.index; });
    return hits.slice(0, limit).map(function (hit) { return hit.entry; });
  }

  function rootUrl() {
    var url = (root.routes && root.routes.root_url) ||
      (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return url.charAt(url.length - 1) === '/' ? url : url + '/';
  }

  function readStorage() {
    try {
      var raw = root.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function writeStorage(entries) {
    try {
      root.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (e) {
      /* stockage indisponible (navigation privée, quota) : on garde le cache mémoire */
    }
  }

  function parseSection(html) {
    var doc = new root.DOMParser().parseFromString(html, 'text/html');
    var script = doc.getElementById(SECTION_ID);
    if (!script) return [];
    var data = JSON.parse(script.textContent);
    return Array.isArray(data) ? data : [];
  }

  function load() {
    if (memory) return Promise.resolve(memory);
    var stored = readStorage();
    if (stored) {
      memory = stored;
      return Promise.resolve(memory);
    }
    if (pending) return pending;
    pending = fetch(rootUrl() + '?section_id=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('faq-search-index: HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        memory = parseSection(html);
        writeStorage(memory);
        return memory;
      })
      .catch(function (error) {
        pending = null;
        if (root.console && root.console.warn) root.console.warn(error);
        return [];
      });
    return pending;
  }

  /* ---------- extrait justificatif ---------- */

  // Segments « lettres/chiffres » d'un mot d'origine (« d'Engrain » → « d », « Engrain »),
  // pour ne surligner que la partie qui matche. \p{L} exige le flag `u` (tous les
  // navigateurs courants) ; repli latin étendu si le moteur ne le connaît pas.
  var SEGMENT_RE;
  try {
    SEGMENT_RE = new RegExp('[\\p{L}\\p{N}]+', 'gu');
  } catch (e) {
    SEGMENT_RE = /[A-Za-z0-9À-ɏḀ-ỿ]+/g;
  }

  // Un mot d'origine « commence par » un mot de la requête si l'un de ses segments
  // normalisés le fait (même règle de préfixe que hasPrefix / match).
  function matchingSegments(word, queryWords) {
    var found = [];
    var re = new RegExp(SEGMENT_RE.source, SEGMENT_RE.flags);
    var m;
    while ((m = re.exec(word)) !== null) {
      var segmentTokens = normalize(m[0]).split(/[^a-z0-9]+/);
      var hit = false;
      for (var i = 0; i < queryWords.length && !hit; i++) {
        hit = hasPrefix(segmentTokens, queryWords[i]);
      }
      if (hit) found.push({ start: m.index, end: m.index + m[0].length });
      if (m[0].length === 0) re.lastIndex++;
    }
    return found;
  }

  function firstHit(words, queryWords) {
    for (var i = 0; i < words.length; i++) {
      if (matchingSegments(words[i], queryWords).length) return i;
    }
    return -1;
  }

  // snippet(entry, query, maxWords) → { text, parts:[{text, mark}], wordCount, source, truncatedStart, truncatedEnd }
  function snippet(entry, query, maxWords) {
    var limit = maxWords || 18;
    var queryWords = tokens(query);
    var answer = String((entry && entry.x) || '').trim();
    var source = 'answer';
    var words = answer ? answer.split(/\s+/) : [];
    var hit = queryWords.length ? firstHit(words, queryWords) : -1;
    if (hit === -1 && !words.length) {
      // pas de réponse : on retombe sur la question
      source = 'question';
      words = String((entry && entry.q) || '').trim().split(/\s+/).filter(Boolean);
      hit = queryWords.length ? firstHit(words, queryWords) : -1;
    }

    var start = 0;
    if (hit > 0) start = Math.max(0, hit - Math.floor((limit - 1) / 2));
    var end = Math.min(words.length, start + limit);
    if (end - start < limit) start = Math.max(0, end - limit);

    var parts = [];
    var buffer = '';
    function flush() {
      if (buffer) parts.push({ text: buffer, mark: false });
      buffer = '';
    }
    var truncatedStart = start > 0;
    var truncatedEnd = end < words.length;
    if (truncatedStart) buffer += '… ';
    for (var i = start; i < end; i++) {
      var word = words[i];
      var segments = queryWords.length ? matchingSegments(word, queryWords) : [];
      var cursor = 0;
      for (var s = 0; s < segments.length; s++) {
        buffer += word.slice(cursor, segments[s].start);
        flush();
        parts.push({ text: word.slice(segments[s].start, segments[s].end), mark: true });
        cursor = segments[s].end;
      }
      buffer += word.slice(cursor);
      if (i < end - 1) buffer += ' ';
    }
    if (truncatedEnd) buffer += ' …';
    flush();

    var text = '';
    for (var p = 0; p < parts.length; p++) text += parts[p].text;
    return {
      text: text,
      parts: parts,
      wordCount: end - start,
      source: source,
      truncatedStart: truncatedStart,
      truncatedEnd: truncatedEnd,
    };
  }

  // Fragment DOM à partir de snippet() : nœuds texte + <mark> (pas d'innerHTML).
  function renderSnippet(result, doc) {
    var d = doc || root.document;
    var fragment = d.createDocumentFragment();
    var parts = (result && result.parts) || [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].mark) {
        var mark = d.createElement('mark');
        mark.textContent = parts[i].text;
        fragment.appendChild(mark);
      } else {
        fragment.appendChild(d.createTextNode(parts[i].text));
      }
    }
    return fragment;
  }

  var api = {
    SECTION_ID: SECTION_ID,
    normalize: normalize,
    tokens: tokens,
    match: match,
    snippet: snippet,
    renderSnippet: renderSnippet,
    load: load,
  };
  root.FaqSearchIndex = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
